import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { ChevronDown, Download, Mail, Repeat } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import AlertBox from '@/components/AlertBox';
import { DataTable, DataTableSearchConfig } from '@/components/DataTable';
import FormFieldCalendar from '@/components/FormFieldCalendar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/use-toast';
import { convertISODateToMMDDYYYY, formatCurrency } from '@/lib/utils';
import { hasFeature } from '@/services/featureService';
import {
  emailStatements,
  exportStatements,
  regenerateStatements,
  useGetStatements,
  useStatementFilterOptions,
} from '@/services/statementService';
import { StatementListItem, StatementListQueryParams } from '@/types/statement.types';
import { DialogDescription } from '@radix-ui/react-dialog';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const regenerateStatementSchema = z
  .object({
    fromDate: z.date({ message: 'Start Date is required' }),
    toDate: z.date({ message: 'End Date is required' }),
  })
  .superRefine((data, ctx) => {
    if (data.fromDate > data.toDate) {
      ctx.addIssue({
        code: 'custom',
        message: 'Start Date must be less than or equal to End Date',
        path: ['toDate'],
      });
    }
  });

type RegenerateStatementForm = z.infer<typeof regenerateStatementSchema>;

type StatementEmailConfirmation = {
  statementIds: string[];
  emails: string[];
};

type RegenerateStatementDialog = {
  clientIds: string[];
  clientNumbers: string[];
  clientName?: string;
  fromDate: Date;
  toDate: Date;
};

const toApiDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseStatementDate = (date: string): Date => {
  return new Date(`${date}T00:00:00`);
};

const normalizeEmailValue = (value: string[] | string | null | undefined): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((email) => email.trim()).filter(Boolean);
  return value
    .split(',')
    .map((email) => email.trim())
    .filter(Boolean);
};

const getStatementEmails = (statement: StatementListItem): string[] => {
  return [...normalizeEmailValue(statement.statementEmails), ...normalizeEmailValue(statement.statementCcEmails)];
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { error?: { details?: unknown; message?: string } } } }).response;
    const details = response?.data?.error?.details;
    if (typeof details === 'string') return details;
    if (response?.data?.error?.message) return response.data.error.message;
  }

  return fallback;
};

const Statements = () => {
  const [selectedStatementIds, setSelectedStatementIds] = useState<string[]>([]);
  const [selectedStatementById, setSelectedStatementById] = useState<Record<string, StatementListItem>>({});
  const [tableData, setTableData] = useState<{ data: StatementListItem[]; rest?: unknown }>();
  const [refreshKey, setRefreshKey] = useState(0);
  const [emailConfirmation, setEmailConfirmation] = useState<StatementEmailConfirmation | null>(null);
  const [regenerateDialog, setRegenerateDialog] = useState<RegenerateStatementDialog | null>(null);
  const lastErrorRef = useRef<Error | null>(null);

  const filterOptionsData = useStatementFilterOptions();
  const canReadStatements = hasFeature('statement.read');
  const canShareStatements = hasFeature('statement.share');
  const canRegenerateStatements = hasFeature('statement.generate');
  const regenerateForm = useForm<RegenerateStatementForm>({
    resolver: zodResolver(regenerateStatementSchema),
    mode: 'all',
  });
  const apiGetWrapper = useCallback((params: Record<string, unknown>) => {
    const apiParams: StatementListQueryParams = {
      ...params,
      pageNo: params.pageNo as number,
      pageSize: params.pageSize as number,
    };
    const result = useGetStatements(apiParams);

    return {
      data: result.data,
      totalRows: result.totalRows,
      summaryTotalRows: result.totalRows,
      isFetching: result.isFetching,
      totalAmountDue: result.totalAmountDue,
      lessThan30DaysDue: result.lessThan30DaysDue,
      past30DaysDue: result.past30DaysDue,
      past60DaysDue: result.past60DaysDue,
      past90DaysDue: result.past90DaysDue,
      isError: result.isError,
      error: result.error,
    };
  }, []);

  const { clientOptions } = useMemo(() => {
    return {
      clientOptions: (filterOptionsData.data?.clients ?? []).map((opt) => ({
        ...opt,
        label1: opt.label1 ?? '',
      })),
    };
  }, [filterOptionsData.data]);

  const summaryData = useMemo(() => {
    const response = tableData?.rest as
      | {
          summaryTotalRows?: number;
          totalAmountDue?: string;
          lessThan30DaysDue?: string;
          past30DaysDue?: string;
          past60DaysDue?: string;
          past90DaysDue?: string;
          isError?: boolean;
          error?: Error | null;
        }
      | undefined;

    return {
      totalRows: response?.summaryTotalRows,
      totalAmountDue: response?.totalAmountDue,
      lessThan30DaysDue: response?.lessThan30DaysDue,
      past30DaysDue: response?.past30DaysDue,
      past60DaysDue: response?.past60DaysDue,
      past90DaysDue: response?.past90DaysDue,
      isError: response?.isError,
      error: response?.error ?? null,
    };
  }, [tableData]);

  useEffect(() => {
    if (summaryData.isError && summaryData.error !== lastErrorRef.current) {
      lastErrorRef.current = summaryData.error;
      toast({
        description: 'Unable to load statements. Please try again.',
        variant: 'destructive',
      });
    }
  }, [summaryData.error, summaryData.isError]);

  useEffect(() => {
    if (regenerateDialog) {
      regenerateForm.reset({
        fromDate: regenerateDialog.fromDate,
        toDate: regenerateDialog.toDate,
      });
    }
  }, [regenerateDialog, regenerateForm]);

  const exportMutation = useMutation({
    mutationFn: exportStatements,
    onMutate: () => {
      toast({
        description: 'Preparing statements for export',
        duration: 3000,
      });
    },
    onError: (error: unknown) => {
      toast({
        description: getErrorMessage(error, 'Unable to export statements. Please try again.'),
        variant: 'destructive',
      });
    },
  });

  const emailMutation = useMutation({
    mutationFn: emailStatements,
    onSuccess: () => {
      toast({ description: 'Email will be sent shortly.' });
      setSelectedStatementIds([]);
      setSelectedStatementById({});
      setEmailConfirmation(null);
    },
    onError: (error: unknown) => {
      toast({
        description: getErrorMessage(error, 'Unable to email statements. Please try again.'),
        variant: 'destructive',
      });
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: regenerateStatements,
    onSuccess: (data) => {
      toast({
        description: data.message ?? 'Statements regenerated successfully.',
      });
      setSelectedStatementIds([]);
      setSelectedStatementById({});
      setRegenerateDialog(null);
      setRefreshKey((prev) => prev + 1);
    },
    onError: (error: unknown) => {
      toast({
        description: getErrorMessage(error, 'Unable to regenerate statements. Please try again.'),
        variant: 'destructive',
      });
    },
  });

  const handleCheckboxChange = useCallback((statement: StatementListItem, isChecked: string | boolean) => {
    if (isChecked) {
      setSelectedStatementIds((prev) => (prev.includes(statement.id) ? prev : [...prev, statement.id]));
      setSelectedStatementById((prev) => ({ ...prev, [statement.id]: statement }));
    } else {
      setSelectedStatementIds((prev) => prev.filter((id) => id !== statement.id));
      setSelectedStatementById((prev) => {
        const next = { ...prev };
        delete next[statement.id];
        return next;
      });
    }
  }, []);

  const handleSelectAllChange = useCallback(
    (isChecked: boolean) => {
      const currentPageStatements = tableData?.data ?? [];
      const currentPageIds = currentPageStatements.map((statement) => statement.id);

      if (isChecked) {
        setSelectedStatementIds((prev) => Array.from(new Set([...prev, ...currentPageIds])));
        setSelectedStatementById((prev) => {
          const next = { ...prev };
          currentPageStatements.forEach((statement) => {
            next[statement.id] = statement;
          });
          return next;
        });
      } else {
        setSelectedStatementIds((prev) => prev.filter((id) => !currentPageIds.includes(id)));
        setSelectedStatementById((prev) => {
          const next = { ...prev };
          currentPageIds.forEach((id) => {
            delete next[id];
          });
          return next;
        });
      }
    },
    [tableData]
  );

  const isCurrentPageSelected = useMemo(() => {
    const currentPageIds = tableData?.data.map((statement) => statement.id) ?? [];
    return currentPageIds.length > 0 && currentPageIds.every((id) => selectedStatementIds.includes(id));
  }, [selectedStatementIds, tableData]);

  const getStatementsByIds = useCallback(
    (statementIds: string[]) => {
      const currentPageStatements = tableData?.data ?? [];
      return statementIds
        .map((id) => selectedStatementById[id] ?? currentPageStatements.find((statement) => statement.id === id))
        .filter((statement): statement is StatementListItem => !!statement);
    },
    [selectedStatementById, tableData]
  );

  const handleExport = useCallback(
    (statementIds: string[]) => {
      if (!statementIds.length) {
        toast({
          description: 'Please select at least one statement to export',
          variant: 'destructive',
        });
        return;
      }

      exportMutation.mutate(statementIds);
    },
    [exportMutation]
  );

  const handleEmailAction = useCallback(
    (statementIds: string[]) => {
      if (!statementIds.length) {
        toast({
          description: 'Please select at least one statement to email',
          variant: 'destructive',
        });
        return;
      }

      const statements = getStatementsByIds(statementIds);

      const statementsMissingEmails = statements.filter((statement) => getStatementEmails(statement).length === 0);
      if (statementsMissingEmails.length > 0) {
        const clientNames = Array.from(new Set(statementsMissingEmails.map((s) => s.clientName ?? s.clientId))).join(
          ', '
        );
        toast({
          description: `No email exists for clients: ${clientNames}. Please configure email addresses before sending.`,
          variant: 'destructive',
        });
        return;
      }

      const emails = Array.from(new Set(statements.flatMap(getStatementEmails)));
      const invalidEmails = emails.filter((email) => !emailRegex.test(email));
      if (invalidEmails.length > 0) {
        toast({
          description: `Invalid email address: ${invalidEmails.join(', ')}`,
          variant: 'destructive',
        });
        return;
      }

      setEmailConfirmation({ statementIds, emails });
    },
    [getStatementsByIds]
  );

  const handleRegenerateAction = useCallback(
    (statementIds: string[]) => {
      if (!statementIds.length) {
        toast({
          description: 'Please select at least one statement to regenerate',
          variant: 'destructive',
        });
        return;
      }

      const statements = getStatementsByIds(statementIds);
      // Map to `clientId` to provide the required array of UUIDs for the API
      const clientIds = Array.from(new Set(statements.map((s) => s.clientId)));
      const statementDates = statements
        .map((statement) => parseStatementDate(statement.date))
        .filter((date) => !Number.isNaN(date.getTime()));
      const today = new Date();
      const fromDate = statementDates.length
        ? new Date(Math.min(...statementDates.map((date) => date.getTime())))
        : today;
      const toDate = statementDates.length
        ? new Date(Math.max(...statementDates.map((date) => date.getTime())))
        : today;

      setRegenerateDialog({
        clientIds,
        clientNumbers: Array.from(new Set(statements.map((s) => s.clientNumber ?? ''))).filter(Boolean),
        clientName: statements.find((s) => clientIds.includes(s.clientId))?.clientName ?? '',
        fromDate,
        toDate,
      });
    },
    [getStatementsByIds]
  );

  const handleRegenerateReview = regenerateForm.handleSubmit((values) => {
    if (!regenerateDialog) return;
    regenerateMutation.mutate({
      clientIds: regenerateDialog.clientIds,
      fromDate: toApiDateString(values.fromDate),
      toDate: toApiDateString(values.toDate),
    });
  });

  const searchConfig: DataTableSearchConfig[] = useMemo(
    () => [
      {
        name: 'clientIds',
        label: 'Client',
        placeholder: 'Select Clients',
        fieldType: 'MULTISELECT',
        multiSelectOptions: clientOptions,
        labelToDisplay: { dropdown: 'label1', selectedOption: 'label2' },
      },
      {
        name: 'fromDate',
        label: 'From Date',
        placeholder: 'Select Date',
        fieldType: 'DATE',
        disabledAt: (date: Date) => date > new Date(),
      },
      {
        name: 'toDate',
        label: 'To Date',
        placeholder: 'Select Date',
        fieldType: 'DATE',
        disabledAt: (date: Date) => date > new Date(),
        dependsOn: {
          field: 'fromDate',
          resetOnChange: true,
          disableBefore: true,
        },
      },
    ],
    [clientOptions]
  );

  const bulkActions = useMemo(
    () =>
      [
        {
          label: 'Export Statements',
          value: 'exportStatements',
          icon: <Download className="w-4 h-4" color="#334155" strokeWidth="1px" />,
          isVisible: true,
          onClick: () => handleExport(selectedStatementIds),
        },
        {
          label: 'Email Statements',
          value: 'emailStatements',
          icon: <Mail className="w-4 h-4" color="#334155" strokeWidth="1px" />,
          isVisible: canShareStatements,
          onClick: () => handleEmailAction(selectedStatementIds),
        },
      ].filter((action) => action.isVisible),
    [canShareStatements, handleEmailAction, handleExport, handleRegenerateAction, selectedStatementIds]
  );

  const summarySection = useMemo(
    () => (
      <div className="w-full flex justify-between flex-col md:flex-row gap-4">
        <div className="w-[50%] flex justify-between flex-col md:justify-start gap-1.5">
          <div className="flex flex-row gap-1">
            <span className="font-[500] text-sm">Total Amount Due of this month: </span>
            <span className="text-[#551175] text-sm font-[500]">
              {summaryData.totalAmountDue ? formatCurrency(parseFloat(summaryData.totalAmountDue)) : '$0.00'}
            </span>
          </div>
          <div className="flex flex-row gap-1">
            <span className="font-[500] text-sm">Total Number of Statements: </span>
            <span className="text-primary text-sm font-[500]">{summaryData.totalRows}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger
              asChild
              className="focus:border-[#1551B2] !border-[1px] !border-primary min-w-[146px] w-fit h-[36px] !text-primary font-[500] text-sm"
            >
              <Button
                variant="outline"
                className="flex flex-row gap-2 align-middle"
                disabled={
                  selectedStatementIds.length === 0 ||
                  exportMutation.isPending ||
                  emailMutation.isPending ||
                  regenerateMutation.isPending
                }
              >
                <span>Bulk Actions</span>
                <ChevronDown className="w-4 h-4" strokeWidth="1px" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 mr-8">
              {bulkActions.map((action) => (
                <DropdownMenuItem
                  key={action.value}
                  disabled={selectedStatementIds.length === 0}
                  onSelect={action.onClick}
                >
                  <div className="!flex !w-full !flex-row justify-between">
                    <span className="text-primary text-sm font-[500]">{action.label}</span>
                    <span>{action.icon}</span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    ),
    [
      bulkActions,
      emailMutation.isPending,
      exportMutation.isPending,
      regenerateMutation.isPending,
      selectedStatementIds.length,
      summaryData,
    ]
  );

  const columns: ColumnDef<StatementListItem>[] = useMemo(() => {
    return [
      {
        accessorKey: 'select',
        header: () => (
          <Checkbox
            id="select-all-statements"
            checked={isCurrentPageSelected}
            onCheckedChange={(checked) => handleSelectAllChange(checked as boolean)}
          />
        ),
        size: 50,
        cell: ({ row }) => {
          const statement = row.original;
          return (
            <div className="flex w-full min-w-[40px]">
              <Checkbox
                id={`statement-${statement.id}`}
                checked={selectedStatementIds.includes(statement.id)}
                onCheckedChange={(checked) => handleCheckboxChange(statement, checked)}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'clientNumber',
        header: 'Client #',
        cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return <span className="mr-5 flex min-w-[100px]">{value ?? '-'}</span>;
        },
      },
      {
        accessorKey: 'date',
        header: 'Date',
        enableSorting: true,
        cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return <span className="mr-5 flex min-w-[100px]">{value ? convertISODateToMMDDYYYY(value) : '-'}</span>;
        },
      },
      {
        accessorKey: 'clientName',
        header: 'Client Name',
        enableSorting: true,
        cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return <span className="mr-5 flex min-w-[160px]">{value ?? '-'}</span>;
        },
      },
      {
        accessorKey: 'amount',
        header: 'Amount',
        enableSorting: true,
        cell: ({ cell }) => {
          const value = cell.getValue() as number | null;

          return <span className="mr-5 flex min-w-[100px]">{formatCurrency(value ?? 0)}</span>;
        },
      },
      {
        accessorKey: 'actions',
        header: '',
        cell: ({ row }) => {
          const statement = row.original;
          return (
            <div className="flex min-w-[120px] flex-row justify-end gap-8">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={`Export statement ${statement.clientName ?? statement.id}`}
                disabled={exportMutation.isPending}
                onClick={() => handleExport([statement.id])}
              >
                <Download className="h-4 w-4" />
              </Button>
              {canShareStatements && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={`Email statement ${statement.clientName ?? statement.id}`}
                  disabled={emailMutation.isPending}
                  onClick={() => handleEmailAction([statement.id])}
                >
                  <Mail className="h-4 w-4" />
                </Button>
              )}
              {canRegenerateStatements && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={`Regenerate statement ${statement.clientName ?? statement.id}`}
                  disabled={regenerateMutation.isPending}
                  onClick={() => handleRegenerateAction([statement.id])}
                >
                  <Repeat className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        },
      },
    ];
  }, [
    canRegenerateStatements,
    canShareStatements,
    emailMutation.isPending,
    exportMutation.isPending,
    handleEmailAction,
    handleExport,
    handleCheckboxChange,
    handleSelectAllChange,
    handleRegenerateAction,
    isCurrentPageSelected,
    regenerateMutation.isPending,
    selectedStatementIds,
  ]);

  if (!canReadStatements) {
    return <div className="text-placeholder">You do not have access to view statements.</div>;
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <DataTable
        toolbar={{
          toolBarTitle: 'Statements',
        }}
        columns={columns}
        apiGet={apiGetWrapper}
        setRows={(data, rest) => setTableData({ data: data as StatementListItem[], rest })}
        search={searchConfig}
        summarySection={summarySection}
        key={refreshKey}
      />

      {emailConfirmation && (
        <AlertBox
          open
          title="Are you sure?"
          description={
            <span>
              Are you sure you want to share the statement with{' '}
              <span className="text-primary font-medium">{emailConfirmation.emails.join(', ')}</span>?
            </span>
          }
          cancelBtnLabel="No"
          confirmBtnLabel="Yes"
          isLoading={emailMutation.isPending}
          onOpenChange={(open) => {
            if (!open) setEmailConfirmation(null);
          }}
          cancelBtnClick={() => setEmailConfirmation(null)}
          confirmBtnClick={() => emailMutation.mutate(emailConfirmation.statementIds)}
        />
      )}

      <Dialog
        open={!!regenerateDialog}
        onOpenChange={(open) => {
          if (!open && !regenerateMutation.isPending) {
            setRegenerateDialog(null);
          }
        }}
      >
        <DialogContent
          className="sm:max-w-md max-w-[40vw] min-w-[50vw] gap-6"
          onInteractOutside={(event) => {
            event.preventDefault();
          }}
        >
          <DialogHeader className="flex gap-6">
            <DialogTitle>{`Regenerate Statement for ${regenerateDialog?.clientNumbers.join(', ')} - ${regenerateDialog?.clientName}`}</DialogTitle>
            <DialogDescription>Select the invoice date range to include.</DialogDescription>
          </DialogHeader>
          <FormProvider {...regenerateForm}>
            <div className="grid grid-cols-2 gap-4">
              <FormFieldCalendar
                name="fromDate"
                label="Start Date"
                placeholder="Select Start Date"
                required
                className="w-[50%]"
                control={regenerateForm.control}
              />
              <FormFieldCalendar
                name="toDate"
                label="End Date"
                placeholder="Select End Date"
                required
                control={regenerateForm.control}
              />
            </div>
          </FormProvider>
          <DialogFooter>
            <div className="flex gap-4">
              <Button
                size="sm"
                variant="outline"
                className="w-24"
                disabled={regenerateMutation.isPending}
                onClick={() => setRegenerateDialog(null)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="w-24"
                disabled={regenerateMutation.isPending}
                onClick={handleRegenerateReview}
              >
                Regenerate
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Statements;
